"use client";

import { useEffect, useMemo, useState } from "react";
import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import {
  DataTable,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  HINT,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  HIndexChart,
  MAX_BARS,
} from "@/components/tools/reference-tools/h-index-chart";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  citationMetrics,
  type HMetrics,
  mQuotient,
  type Paper,
  parseCitationList,
  type YearCount,
} from "@/lib/tool-kit/reference-tools/h-index";

/** English strings. A translated content file overrides these through `ui`. */
export const H_INDEX_CALCULATOR_STRINGS = {
  inputLabel: "Citation counts — one paper per line, or “title, citations”",
  inputPlaceholder:
    "Paper one, 48\nPaper two, 33\nPaper three, 30\n21\n17\n12\n9\n8\n5\n3\n1\n0",
  inputHint:
    "A tab, comma, semicolon or bar can separate a title from its count, so two columns pasted from a spreadsheet work. A count may use a thousands separator (1,234).",
  firstYearLabel: "Year of your first publication (for the m-quotient)",
  firstYearPlaceholder: "2010",
  currentYearLabel: "Year to measure in",
  yearCountLabel: "Count the career length as",
  yearElapsed: "Years since the first paper (Hirsch)",
  yearInclusive: "Calendar years, counting both ends",
  emptyPrompt: "Paste a citation count for each paper to see the h-index.",
  rejected:
    "one:Line {lines} has no citation count and was skipped.|other:Lines {lines} have no citation count and were skipped.",
  results: "Your metrics",
  h: "h-index",
  g: "g-index",
  i10: "i10-index",
  papers: "Papers",
  total: "Total citations",
  mean: "Citations per paper",
  m: "m-quotient",
  workingHeading: "The working",
  hWorking:
    "h = {h}: the paper ranked {h} has {hCites} citations, at least {h}; the paper ranked {next} has {nextCites}, fewer than {next}.",
  hWorkingAll:
    "h = {h}: all {h} papers have at least {h} citations each, so adding papers could still raise it.",
  hWorkingZero: "h = 0: no paper has been cited even once.",
  gWorking:
    "g = {g}: the top {g} papers have {sum} citations between them, at least {g}² = {square}.",
  gCapped:
    "g is capped at the number of papers listed. Egghe's original definition pads the list with uncited papers and would give g = {uncapped}.",
  i10Working: "i10 = {i10}: papers with at least 10 citations.",
  mWorking: "m = h ÷ years = {h} ÷ {years} = {m}.",
  mNeedsYear: "Add the year of your first publication to get the m-quotient.",
  mTooSoon:
    "The m-quotient needs at least one year since the first publication.",
  hCoreHeading:
    "one:The h-core: the paper that counts towards h|other:The h-core: the {count} papers that count towards h",
  rank: "Rank",
  paper: "Paper",
  citations: "Citations",
  untitled: "Line {line}",
  chartLabel: "Papers ranked by citations, with the h line",
  chartDescription:
    "Bar chart of {count} papers sorted by citations. A dashed line marks rank = citations; the h-index, {h}, is the last rank whose bar reaches it.",
  chartTop: "The chart shows the {max} most-cited papers.",
  axisRank: "Paper rank",
  axisCitations: "Citations",
  hLine: "rank = citations",
  hMark: "h = {h}",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy data",
  copied: "Copied!",
  exportFailed: "The image could not be exported. Try the SVG instead.",
  showDataTable: "Show the data as a table",
} as const;

type Strings = typeof H_INDEX_CALCULATOR_STRINGS;
type Format = ReturnType<typeof useToolNumber>;

function paperName(paper: Paper, strings: Strings): string {
  return paper.title ?? t(strings.untitled, { line: paper.line });
}

function hSentence(metrics: HMetrics, strings: Strings, fmt: Format): string {
  const { h, ranked } = metrics;
  if (h === 0) {
    return strings.hWorkingZero;
  }
  if (h === ranked.length) {
    return t(strings.hWorkingAll, { h: fmt(h) });
  }
  return t(strings.hWorking, {
    h: fmt(h),
    hCites: fmt(ranked[h - 1].citations),
    next: fmt(h + 1),
    nextCites: fmt(ranked[h].citations),
  });
}

function gSentences(
  metrics: HMetrics,
  strings: Strings,
  fmt: Format
): string[] {
  const { g, gUncapped, ranked } = metrics;
  if (g === 0) {
    return [];
  }
  const sum = ranked
    .slice(0, g)
    .reduce((total, paper) => total + paper.citations, 0);
  const lines = [
    t(strings.gWorking, { g: fmt(g), sum: fmt(sum), square: fmt(g * g) }),
  ];
  if (gUncapped > g) {
    lines.push(t(strings.gCapped, { uncapped: fmt(gUncapped) }));
  }
  return lines;
}

export default function HIndexCalculatorWidget() {
  const strings = useToolUi(H_INDEX_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [firstYear, setFirstYear] = useState("");
  const [currentYear, setCurrentYear] = useState("");
  const [yearCount, setYearCount] = useState<YearCount>("elapsed");

  useEffect(() => {
    setCurrentYear(String(new Date().getFullYear()));
  }, []);

  const parsed = useMemo(() => parseCitationList(text), [text]);
  const metrics = useMemo(() => citationMetrics(parsed.papers), [parsed]);
  const first = parseIntegerInput(firstYear);
  const now = parseIntegerInput(currentYear);
  const m =
    first !== null && now !== null
      ? mQuotient(metrics.h, first, now, yearCount)
      : null;
  let mNote: string = strings.mNeedsYear;
  if (m) {
    mNote = t(strings.mWorking, {
      h: fmt(metrics.h),
      years: fmt(m.years),
      m: fmt(m.m, { maximumFractionDigits: 2 }),
    });
  } else if (first !== null && now !== null) {
    mNote = strings.mTooSoon;
  }
  const rejected = parsed.rejected;
  const core = metrics.ranked.slice(0, metrics.h);

  return (
    <section className="space-y-6">
      <TextAreaField
        hint={strings.inputHint}
        id="hic-input"
        label={strings.inputLabel}
        onChange={setText}
        placeholder={strings.inputPlaceholder}
        rows={10}
        value={text}
      />
      <div className="grid gap-4 sm:grid-cols-3">
        <TextField
          id="hic-first"
          label={strings.firstYearLabel}
          numeric
          onChange={setFirstYear}
          placeholder={strings.firstYearPlaceholder}
          value={firstYear}
        />
        <TextField
          id="hic-now"
          label={strings.currentYearLabel}
          numeric
          onChange={setCurrentYear}
          value={currentYear}
        />
        <SelectField<YearCount>
          id="hic-count"
          label={strings.yearCountLabel}
          onChange={setYearCount}
          options={[
            { value: "elapsed", label: strings.yearElapsed },
            { value: "inclusive", label: strings.yearInclusive },
          ]}
          value={yearCount}
        />
      </div>
      <div aria-live="polite" className="space-y-6">
        {metrics.papers === 0 ? <Notice>{strings.emptyPrompt}</Notice> : null}
        {rejected.length > 0 ? (
          <p className={HINT}>
            {tPlural(strings.rejected, locale, rejected.length, {
              lines: rejected.map((line) => fmt(line.line)).join(", "),
            })}
          </p>
        ) : null}
        {metrics.papers > 0 ? (
          <div className={`${RESULT_CARD} space-y-4`}>
            <h2 className={WIDGET_H2}>{strings.results}</h2>
            <StatGrid>
              <StatCard emphasis label={strings.h} value={fmt(metrics.h)} />
              <StatCard label={strings.g} value={fmt(metrics.g)} />
              <StatCard label={strings.i10} value={fmt(metrics.i10)} />
              <StatCard label={strings.papers} value={fmt(metrics.papers)} />
              <StatCard label={strings.total} value={fmt(metrics.total)} />
              <StatCard
                label={strings.mean}
                value={fmt(metrics.mean, { maximumFractionDigits: 1 })}
              />
              <StatCard
                label={strings.m}
                value={m ? fmt(m.m, { maximumFractionDigits: 2 }) : "—"}
              />
            </StatGrid>
            <div className="space-y-2">
              <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
              <ul className="list-disc space-y-1 pl-5 text-foreground text-sm">
                <li>{hSentence(metrics, strings, fmt)}</li>
                {gSentences(metrics, strings, fmt).map((line) => (
                  <li key={line}>{line}</li>
                ))}
                <li>{t(strings.i10Working, { i10: fmt(metrics.i10) })}</li>
                <li>{mNote}</li>
              </ul>
            </div>
          </div>
        ) : null}
        {metrics.papers > 0 ? (
          <ChartFigure
            apa={false}
            data={toTsv([
              [strings.rank, strings.paper, strings.citations],
              ...metrics.ranked.map((paper, index) => [
                index + 1,
                paperName(paper, strings),
                paper.citations,
              ]),
            ])}
            figureNumber="1"
            filename="h-index-chart"
            labels={strings}
            note=""
            table={{
              head: [strings.rank, strings.paper, strings.citations],
              rows: metrics.ranked
                .slice(0, MAX_BARS)
                .map((paper, index) => [
                  fmt(index + 1),
                  paperName(paper, strings),
                  fmt(paper.citations),
                ]),
            }}
            title={strings.chartLabel}
          >
            <HIndexChart
              citationsTitle={strings.axisCitations}
              description={t(strings.chartDescription, {
                count: fmt(Math.min(metrics.papers, MAX_BARS)),
                h: fmt(metrics.h),
              })}
              fmt={fmt}
              h={metrics.h}
              hLabel={t(strings.hMark, { h: fmt(metrics.h) })}
              id="hic-chart"
              label={strings.chartLabel}
              lineLabel={strings.hLine}
              ranked={metrics.ranked}
              rankTitle={strings.axisRank}
            />
          </ChartFigure>
        ) : null}
        {metrics.papers > MAX_BARS ? (
          <p className={HINT}>{t(strings.chartTop, { max: fmt(MAX_BARS) })}</p>
        ) : null}
        {core.length > 0 ? (
          <div className="space-y-3">
            <h3 className={WIDGET_H3}>
              {tPlural(strings.hCoreHeading, locale, core.length, {
                count: fmt(core.length),
              })}
            </h3>
            <DataTable
              head={[strings.rank, strings.paper, strings.citations]}
              rows={core.map((paper, index) => [
                fmt(index + 1),
                paperName(paper, strings),
                fmt(paper.citations),
              ])}
            />
          </div>
        ) : null}
      </div>
    </section>
  );
}
